import React from 'react';

import type { CreateDepartmentRequest } from '@vlabs/api/artemy/model';
import validate from '@vlabs/shared/validators';
import { Button } from '@vlabs/uikit/components/controls/button';
import { Input } from '@vlabs/uikit/components/controls/input';
import { Textarea } from '@vlabs/uikit/components/controls/textarea/Textarea';
import { useForm } from 'react-hook-form';

import st from './EditDepartmentInfoForm.module.sass';
import { useEditDepartmentInfoFormTranslation } from './i18n';

export type EditDepartmentInfoFormValues = Pick<CreateDepartmentRequest, 'name' | 'description'>;

interface EditDepartmentInfoFormProps {
  defaultValues?: EditDepartmentInfoFormValues;
  onSubmit: (values: EditDepartmentInfoFormValues) => void | Promise<void>;
  onCancel?: VoidFunction;
}

function EditDepartmentInfoForm({
  defaultValues = undefined,
  onSubmit,
  onCancel = undefined,
}: EditDepartmentInfoFormProps): React.JSX.Element {
  const { t } = useEditDepartmentInfoFormTranslation();
  const form = useForm({ defaultValues });

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div
        className={st.EditDepartmentInfoForm}
        data-testid="EditDepartmentInfoForm"
      >
        <h5>{t('title')}</h5>

        <div className={st.DataWrapper}>
          <div className={st.InputWrapper}>
            <Input
              autoFocus
              id="name"
              label={t('fields.name')}
              required
              {...form.register('name', {
                required: validate.required(),
                minLength: validate.minLength(3),
                maxLength: validate.maxLength(128),
              })}
              errors={form.formState.errors}
            />
          </div>

          <div className={st.TextareaWrapper}>
            <Textarea
              id="description"
              label={t('fields.description')}
              {...form.register('description', {
                maxLength: validate.maxLength(512),
              })}
              errors={form.formState.errors}
            />
          </div>
        </div>

        <div className={st.Actions}>
          <Button
            className={st.Button}
            type="submit"
          >
            {t('submit')}
          </Button>

          <Button
            className={st.Button}
            kind="secondary"
            onClick={onCancel}
            type="button"
            variant="outlined"
          >
            {t('cancel')}
          </Button>
        </div>
      </div>
    </form>
  );
}

EditDepartmentInfoForm.displayName = 'EditDepartmentInfoForm';

export { EditDepartmentInfoForm };
